import { useCallback, useEffect, useRef, useState } from "react";
import { fetchTodayCadence, type CadenceDaySummary } from "../api/cadence.ts";
import { logout } from "../api/client.js";
import { disconnectSyncSocket } from "../api/sync.js";
import { useAppActions } from "../app/AppProvider.tsx";
import { useToast } from "../components/ToastProvider.tsx";
import type { PanelName } from "../types.ts";
import {
  nextSideNavCollapsed,
  parseSideNavCollapsed,
  SIDE_NAV_COLLAPSED_STORAGE_KEY,
} from "./Shell.helpers.ts";
import { MainPanel } from "./shell/MainPanel.tsx";
import { JampackNavbar } from "./shell/JampackNavbar.tsx";
import { JampackSidebar } from "./shell/JampackSidebar.tsx";
import { CADENCE_TODAY_REFRESH_EVENT } from "./cadence-calendar/events.ts";
import { chatBadge, countBadge } from "./shell/nav.ts";
import { deriveShellStatus } from "./shell/status.ts";

export function Shell() {
  const { state, dispatch, patch } = useAppActions();
  const { showToast } = useToast();
  const [navCollapsed, setNavCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    return parseSideNavCollapsed(window.localStorage.getItem(SIDE_NAV_COLLAPSED_STORAGE_KEY));
  });
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [calendarBadge, setCalendarBadge] = useState<string | null>(null);
  const cadenceToastShownRef = useRef(false);
  const isAdmin = state.user?.role === "admin";
  const status = deriveShellStatus(state);
  const unreadBadge = chatBadge(state.chats);

  const applyTodayCadence = useCallback((summary: CadenceDaySummary, notify: boolean) => {
    const pending = Number(summary.pending || 0);
    setCalendarBadge(countBadge(pending));
    if (notify && pending > 0 && !cadenceToastShownRef.current) {
      cadenceToastShownRef.current = true;
      showToast(`Voce tem ${pending} lead${pending === 1 ? "" : "s"} para cadencia hoje`, "info");
    }
  }, [showToast]);

  const refreshTodayCadence = useCallback(async (notify = false) => {
    try {
      applyTodayCadence(await fetchTodayCadence(), notify);
    } catch {
      setCalendarBadge(null);
    }
  }, [applyTodayCadence]);

  useEffect(() => {
    try {
      window.localStorage.setItem(SIDE_NAV_COLLAPSED_STORAGE_KEY, String(navCollapsed));
    } catch {
      /* noop */
    }
  }, [navCollapsed]);

  useEffect(() => {
    void refreshTodayCadence(true);
    const onCadenceRefresh = (event: Event) => {
      const detail = (event as CustomEvent<CadenceDaySummary | undefined>).detail;
      if (detail) applyTodayCadence(detail, false);
      else void refreshTodayCadence(false);
    };
    window.addEventListener(CADENCE_TODAY_REFRESH_EVENT, onCadenceRefresh);
    return () => window.removeEventListener(CADENCE_TODAY_REFRESH_EVENT, onCadenceRefresh);
  }, [applyTodayCadence, refreshTodayCadence]);

  useEffect(() => {
    if (!mobileSidebarOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileSidebarOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [mobileSidebarOpen]);

  useEffect(() => {
    if (state.panel === "admin" && !isAdmin) {
      dispatch({ type: "SET_PANEL", panel: "kanban" });
    }
  }, [dispatch, isAdmin, state.panel]);

  function setPanel(panel: PanelName) {
    if (panel === "admin" && !isAdmin) return;
    dispatch({ type: "SET_PANEL", panel });
    setMobileSidebarOpen(false);
  }

  function onLogout() {
    disconnectSyncSocket();
    logout();
  }

  const appClass = [
    "hk-app",
    navCollapsed ? "nav-collapsed" : "",
    mobileSidebarOpen ? "sidebar-open" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={appClass}>
      <JampackNavbar
        status={status.status}
        phone={status.phone}
        channelLabel={status.channelLabel}
        query={state.query || ""}
        userEmail={state.userEmail || ""}
        isAdmin={isAdmin}
        onQueryChange={(query) => patch({ query })}
        onLogout={onLogout}
        onToggleSidebar={() => setMobileSidebarOpen((open) => !open)}
      />
      <JampackSidebar
        activePanel={state.panel}
        isAdmin={isAdmin}
        collapsed={navCollapsed}
        status={status.status}
        chatBadge={unreadBadge}
        calendarBadge={calendarBadge}
        onSelect={setPanel}
        onToggleCollapsed={() => setNavCollapsed((current) => nextSideNavCollapsed(current))}
      />
      <div className="hk-sidebar-backdrop" onClick={() => setMobileSidebarOpen(false)} />
      <div className="hk-content">
        <div className="hk-content-inner">
          <MainPanel panel={state.panel} isAdmin={isAdmin} />
        </div>
        <footer className="hk-footer">
          <span>© {new Date().getFullYear()} CRM · Atendimento &amp; IA</span>
          <span>Todos os direitos reservados</span>
        </footer>
      </div>
    </div>
  );
}
